import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { isFinanceVertical } from '@rma/generic/domain/helpers';
import { appendToRmaBaseUrl } from '@rma/generic/ng/util';
import { Environment } from '@rma/public/generic/util/environment';
import {
  AggregateRating,
  Event,
  FinancialService,
  GeoCoordinates,
  Organization,
  PostalAddress,
  Rating,
  RealEstateAgent,
  Residence,
  Review,
  WebSite,
  WithContext,
} from 'schema-dts';
import {
  AddressLinkingData,
  AgentLinkingData,
  EventLinkingData,
  LinkingData,
  LinkingDataMap,
  LinkingDataTypes,
  ResidenceLinkingData,
  ReviewLinkingData,
  WebsiteLinkingData,
} from '../domain';
import { typedEntries } from '../util-typed-entries/typed-entries';

@Injectable({
  providedIn: 'root',
})
export class LinkingDataService {
  private readonly environment = inject(Environment);
  private readonly rmaBaseUrl = inject(appendToRmaBaseUrl);
  private readonly translationService = inject(TranslocoService);

  public readonly schemaContext = 'https://schema.org';

  public generateLdSchema(config?: LinkingData | null): LinkingDataTypes[] | undefined {
    return config
      ? typedEntries(config)
          .filter(([_, value]) => Boolean(value))
          .reduce((accum, [key, data]) => {
            const ld = this.handleType(key, data);
            return Array.isArray(ld) ? [...accum, ...ld] : [...accum, ld];
          }, [] as LinkingDataTypes[])
      : undefined;
  }

  private handleType<K extends keyof LinkingDataMap>(type: K, data: LinkingDataMap[K]): LinkingDataTypes | LinkingDataTypes[] {
    switch (type) {
      case 'salesAgent':
        return this.buildAgent(data as AgentLinkingData, 'RealEstateAgent');
      case 'financeAgent':
        return this.buildAgent(data as AgentLinkingData, 'FinancialService');
      case 'events':
        return (data as EventLinkingData[]).map((events) => this.buildEventSchemaWithContext(events));
      case 'residences':
        return (data as ResidenceLinkingData[]).map((residences) => this.buildResidenceSchemaWithContext(residences));
      case 'reviews':
        return (data as ReviewLinkingData[]).map((reviews) => this.buildReviewSchemaWithContext(reviews));
      case 'website':
        return this.buildWebsiteSchema(data as WebsiteLinkingData);
      default:
        throw new Error(`LinkingDataService could not handle ${type}`);
    }
  }

  private buildUrl(url?: string): string | undefined {
    if (!url) {
      return undefined;
    }
    if (url.startsWith('http')) {
      return url;
    }
    return this.rmaBaseUrl(url);
  }

  private buildAgent(
    data: AgentLinkingData,
    type: 'RealEstateAgent' | 'FinancialService',
  ): WithContext<RealEstateAgent | FinancialService> {
    return {
      '@context': this.schemaContext,
      '@type': type,
      name: data.name,
      url: this.buildUrl(data.url),
      logo: data.logo,
      telephone: data.telephone,
      review: data.reviews?.map((r) => this.buildReviewSchema(r, false)),
      address: this.buildPostalAddressSchema(data.address),
      image: data.image,
      priceRange: data.priceRange,
      aggregateRating: this.buildReviewAggregateSchema(data.aggregateRating),
      sameAs: this.undefinedIfEmptyArray(data.sameAs ?? []),
      areaServed: this.undefinedIfEmptyArray(data.areaServed ?? []),
      awards: this.undefinedIfEmptyArray(data.awards ?? []),
    };
  }

  private undefinedIfEmptyArray(array: string[]) {
    const cleanedArray = array.filter((s) => Boolean(s));
    if (cleanedArray.length) {
      return cleanedArray;
    }

    return undefined;
  }

  private buildReviewAggregateSchema(review?: { rating?: number; reviewCount?: number }): AggregateRating | undefined {
    return !review || !review.rating || !review.reviewCount
      ? undefined
      : {
          '@type': 'AggregateRating',
          ratingValue: review.rating,
          bestRating: 5,
          worstRating: 1,
          reviewCount: review.reviewCount,
        };
  }

  private buildPostalAddressSchema(address?: AddressLinkingData): PostalAddress | undefined {
    return address
      ? {
          '@type': 'PostalAddress',
          streetAddress: address.streetAddress,
          addressLocality: address.addressLocality,
          addressRegion: address.addressRegion,
          postalCode: address.postalCode,
          addressCountry: address.addressCountry,
        }
      : undefined;
  }

  private buildEventSchemaWithContext(event: EventLinkingData): WithContext<Event> {
    return {
      '@context': this.schemaContext,
      '@type': 'Event',
      location: this.buildResidenceSchemaWithContext(event.residence),
      name: event.eventType,
      startDate: event.eventDate.toISOString(),
      url: this.buildUrl(event.canonicalUrl),
    };
  }

  private buildResidenceSchemaWithContext(residence: ResidenceLinkingData): WithContext<Residence> {
    return {
      '@context': this.schemaContext,
      '@type': 'Residence',
      image: residence.imageUrl,
      description: residence.description,
      geo: this.buildGeoCoordinates(residence.address),
      name: residence.address?.streetAddress,
      address: this.buildPostalAddressSchema(residence.address),
    };
  }

  private buildReviewSchemaWithContext(review: ReviewLinkingData): WithContext<Review> | undefined {
    const reviewLd = this.buildReviewSchema(review);
    return reviewLd ? { '@context': this.schemaContext, ...reviewLd } : reviewLd;
  }

  private buildReviewSchema(review: ReviewLinkingData, includeItemReviewed = true): Review {
    const reviewRating: Rating = {
      '@type': 'Rating',
      ratingValue: review.rating ?? 0,
      bestRating: 5,
      worstRating: 0,
    };

    const userType = this.getReviewerType(review.userType ?? 'Unknown');

    return {
      '@type': 'Review',
      datePublished: review.reviewDate?.toISOString(),
      headline: review.title,
      itemReviewed: includeItemReviewed ? this.itemReviewed(review, this.buildUrl(review.agent.profileUrl) ?? '') : undefined,
      publisher: this.getPublisherInfo(),
      reviewBody: review.description,
      reviewRating,
      author: {
        '@type': 'Person',
        name: (review.reviewerName ?? userType)?.trim() || userType,
      },
    };
  }

  private buildGeoCoordinates(coords?: { latitude?: number; longitude?: number }): GeoCoordinates | undefined {
    if (!coords || !coords.latitude || !coords.longitude) {
      return undefined;
    }

    return {
      '@type': 'GeoCoordinates',
      latitude: coords.latitude,
      longitude: coords.longitude,
    };
  }

  private getReviewerType(userType: string): string {
    return this.translationService.translate(`reviewer-type.${userType.toLowerCase()}`);
  }

  private getPublisherInfo(): Organization {
    return {
      '@type': 'Organization',
      name: 'RateMyAgent',
      url: this.rmaBaseUrl(),
      logo: this.rmaBaseUrl('/assets/images/logo-black.png'),
      description:
        "ratemyagent.com.au is Australia's leading real estate agent and agency review website, helping sellers make informed decisions about choosing an estate agent.",
      sameAs: [this.environment.social.facebookUrl, this.environment.social.twitterUrl, this.environment.social.linkedinUrl],
    };
  }

  // this should be converted to agent
  private itemReviewed(review: ReviewLinkingData, agentProfileUrl: string): FinancialService | RealEstateAgent {
    return {
      '@type': isFinanceVertical(review.type) ? 'FinancialService' : 'RealEstateAgent',
      // should we have this on the other one?
      '@id': agentProfileUrl,
      name: review.agent.name,
      url: agentProfileUrl,
      image: review.agent.avatarUrl,
      telephone: review.agent.phone,
      address: this.buildPostalAddressSchema(review.agent.agencyAddress),
    };
  }

  private buildWebsiteSchema(data: WebsiteLinkingData): WithContext<WebSite> {
    return {
      '@context': this.schemaContext,
      '@type': 'WebSite',
      name: data.name,
      url: data.url,
    };
  }
}
